'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import Link from 'next/link';
import { FC, useRef } from 'react';
import InlineTags from '@/components/BlogPage/InlineTags';
import { BlogPostDto } from '@/components/BlogPage/types';
import Image from '@/components/Image';
import { Typography } from '@/components/shared/Typography';
import VimeoPlayer from '@/components/shared/VimeoPlayer';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useBlogPost } from '@/hooks/blog/useBlogPost';
import { useVimeoViewportPlayback } from '@/hooks/useVimeoViewportPlayback';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
interface Props {
  post: BlogPostDto;
  className?: string;
  showTags?: boolean;
  setIsCustomCursorVisible?: (value: boolean) => void;
}
const LatestPost: FC<Props> = t0 => {
  const $ = _c(38);
  const {
    post,
    className,
    showTags: t1,
    setIsCustomCursorVisible
  } = t0;
  const showTags = t1 === undefined ? true : t1;
  const {
    cardTitle,
    media,
    isVideo,
    href
  } = useBlogPost(post);
  const wrapperRef = useRef(null);
  const vimeoPlaying = useVimeoViewportPlayback(wrapperRef);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!wrapperRef.current) {
        return;
      }
      gsap.fromTo(wrapperRef.current, {
        opacity: 0,
        yPercent: 20
      }, {
        opacity: 1,
        yPercent: 0,
        duration: 1.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: wrapperRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });
    };
    t3 = {
      scope: wrapperRef
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useGSAP(t2, t3);
  const t4 = Boolean(setIsCustomCursorVisible) && css.hideDefaultCursor;
  let t5;
  if ($[2] !== className || $[3] !== t4) {
    t5 = clsx(css.wrapper, className, t4);
    $[2] = className;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  let t7;
  if ($[5] !== setIsCustomCursorVisible) {
    t6 = () => setIsCustomCursorVisible?.(true);
    t7 = () => setIsCustomCursorVisible?.(false);
    $[5] = setIsCustomCursorVisible;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Typography variant="body-m" responsive={false} className={css.newLabel}>New</Typography>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== cardTitle) {
    t9 = <Typography className={css.title} variant="h4" tag="h2"><StrapiRichContent content={cardTitle} /></Typography>;
    $[9] = cardTitle;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== post.preview) {
    t10 = post.preview?.description ? <Typography variant="body-l"><StrapiRichContent content={post.preview.description} /></Typography> : null;
    $[11] = post.preview;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== post.tags || $[14] !== showTags) {
    t11 = showTags ? <InlineTags tags={post.tags} className={css.tagsWrapper} /> : null;
    $[13] = post.tags;
    $[14] = showTags;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== post.timeToRead) {
    t12 = post.timeToRead ? <Typography className={css.timeToRead} variant="body-m"><span>{post.timeToRead}</span><img src="/assets/icons/arrow-link.svg" width={12} height={12} alt="" /></Typography> : null;
    $[16] = post.timeToRead;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11 || $[19] !== t12) {
    t13 = <div className={css.bottomInfo}>{t11}{t12}</div>;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t10 || $[22] !== t13 || $[23] !== t9) {
    t14 = <div className={css.infoWrapper}>{t8}{t9}{t10}{t13}</div>;
    $[21] = t10;
    $[22] = t13;
    $[23] = t9;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== isVideo || $[26] !== media || $[27] !== vimeoPlaying) {
    t15 = isVideo && media ? <div className={css.mediaWrapper}><VimeoPlayer className={css.media} url={media.url} playing={vimeoPlaying} /></div> : media && "width" in media && <div className={css.mediaWrapper}><Image className={css.media} src={media.url} width={media.width} height={media.height} alt={media.alternativeText || ""} quality={100} /></div>;
    $[25] = isVideo;
    $[26] = media;
    $[27] = vimeoPlaying;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15) {
    t16 = <article className={css.article}>{t14}{t15}</article>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== href || $[33] !== t16 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7) {
    t17 = <Link ref={wrapperRef} href={href} className={t5} onMouseEnter={t6} onMouseLeave={t7}>{t16}</Link>;
    $[32] = href;
    $[33] = t16;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
};
export default LatestPost;